import Header from '../common/Header';
import Footer from '../common/Footer';
import SeoHelmet from '../common/SeoHelmet';

const PrivacyPolicy = () => {
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Privacy Policy"
        description="How GeoCalc.app collects, uses, and protects personal data, including subscriptions processed via Polar, data retention, rights, and contact details."
        url="https://www.geocalc.app/privacy"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Privacy Policy', url: 'https://www.geocalc.app/privacy' },
        ]}
      />

      <Header />
      <div className="privacyPolicy">
        <h1>Privacy Policy for GeoCalc.app</h1>

        <p>
          This Privacy Policy explains what personal data GeoCalc.app (“GeoCalc”, “we”, “us”) collects, how we use it, and the
          rights available to you. It applies to geocalc.app and any pages under that domain (the “Site”) as well as our tools and
          services (the “Services”).
        </p>

        <h2>What We Collect</h2>
        <ul>
          <li>
            <strong>Account &amp; contact data:</strong> name, email address, and account credentials if you sign up or contact
            us.
          </li>
          <li>
            <strong>Usage data:</strong> events such as pages viewed, tool usage counts, timestamps, approximate location derived
            from IP (city/country level), device/browser information. We do not build personal profiles from this data.
          </li>
          <li>
            <strong>Support communications:</strong> content of messages you send to{' '}
            <a href="mailto:info@geocalc.app">info@geocalc.app</a>.
          </li>
        </ul>

        <h2>Payments via Polar</h2>
        <p>
          We use <strong>Polar</strong> (“Polar”) to manage subscriptions. When you subscribe, checkout occurs on Polar’s website.
          Polar and its payment processors collect and process your payment information as independent controllers for fraud
          prevention and regulatory compliance.
        </p>
        <ul>
          <li>
            <strong>What we receive from Polar:</strong> subscriber contact details (e.g., name, email), plan, subscription
            status, and transaction metadata (amount, currency, timestamps, country, VAT ID where applicable). We do <em>not</em>{' '}
            receive or store full payment card numbers.
          </li>
          <li>
            <strong>How we use it:</strong> to provide and manage your subscription, deliver account support, invoicing, and
            service notifications.
          </li>
          <li>
            <strong>Legal bases:</strong> contract (to provide the subscription), legitimate interests (security, fraud
            prevention, service analytics), and legal obligation (tax/accounting).
          </li>
          <li>
            <strong>Retention:</strong> we retain subscription records while your subscription is active and as required by
            tax/accounting laws. Polar retains payment records per its own legal obligations.
          </li>
          <li>
            <strong>Cookies/widgets:</strong> Checkout occurs on Polar’s site. We do not embed Polar checkout scripts on our
            pages; Polar may set cookies on its own domain per its policies.
          </li>
        </ul>

        <h2>Optional Sponsorships</h2>
        <p>
          We do not run third-party advertising. If we feature sponsorships in the future, they will be labeled. We may share
          de-identified, aggregated metrics with sponsors (e.g., total users, tool usage). No payment card data is shared with
          sponsors.
        </p>

        <h2>How We Use Data</h2>
        <ul>
          <li>Provide, maintain, and improve the Services.</li>
          <li>Authenticate users, secure the Site, prevent misuse and fraud.</li>
          <li>Operate billing and subscriptions via Polar.</li>
          <li>Respond to inquiries and provide support.</li>
          <li>Comply with applicable laws (e.g., tax/accounting).</li>
        </ul>

        <h2>Sharing</h2>
        <ul>
          <li>
            <strong>Service providers:</strong> infrastructure, analytics, error monitoring, email delivery, and billing (Polar).
            Providers process data under contracts that limit their use to our instructions.
          </li>
          <li>
            <strong>Legal and safety:</strong> to comply with law, enforce terms, or protect rights, security, or property.
          </li>
          <li>
            <strong>Business transfers:</strong> in connection with a merger, acquisition, or asset sale. We will continue to
            protect your data consistent with this policy.
          </li>
        </ul>

        <h2>Cookies and Similar Technologies</h2>
        <p>
          We use cookies or local storage for essential functions (e.g., session, preferences) and basic analytics. You can
          control cookies through your browser settings. Disabling essential cookies may affect functionality.
        </p>

        <h2>Data Retention</h2>
        <p>
          We retain personal data only as long as necessary for the purposes described above, including legal, accounting, or
          reporting requirements. Usage logs are kept for a limited period for security and diagnostics.
        </p>

        <h2>International Transfers</h2>
        <p>
          We may process data outside your country. Where required, we implement appropriate safeguards (e.g., Standard
          Contractual Clauses) to protect personal data during transfers.
        </p>

        <h2>Your Rights</h2>
        <p>
          Subject to applicable law, you may have the right to access, correct, delete, or receive a copy of your personal data,
          and to object to or restrict certain processing. You can also withdraw consent where processing is based on consent. To
          exercise rights, contact <a href="mailto:info@geocalc.app">info@geocalc.app</a>. We will verify requests and respond as
          required by law.
        </p>

        <h2>Children’s Privacy</h2>
        <p>
          GeoCalc is not directed to children and does not knowingly collect personal information from children under 13. If you
          believe a child has provided personal data to us, contact <a href="mailto:info@geocalc.app">info@geocalc.app</a> and we
          will delete it.
        </p>

        <h2>Security</h2>
        <p>
          We use reasonable technical and organizational measures to protect personal data. No method of transmission or storage
          is perfectly secure; residual risk remains.
        </p>

        <h2>Third-Party Links</h2>
        <p>
          The Site may contain links to third-party sites. We are not responsible for their content or privacy practices. Review
          their policies before providing data.
        </p>

        <h2>Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated “Last updated”
          date. Material changes may be communicated by email or in-app notice.
        </p>

        <h2>Contact</h2>
        <p>
          Questions or requests: <a href="mailto:info@geocalc.app">info@geocalc.app</a>
        </p>

        <p>
          <em>Last updated: 15/Otober/2025</em>
        </p>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
